import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { 
  Search, Users, Phone, Mail, MapPin, Car, ChevronRight, 
  History, IndianRupee, Calendar, X, Edit2, Save
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Customers = () => {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerJobs, setCustomerJobs] = useState([]);
  const [customerVehicles, setCustomerVehicles] = useState([]);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});

  useEffect(() => {
    fetchCustomers();
  }, [searchQuery]);

  const fetchCustomers = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('garage_token');
      const params = new URLSearchParams();
      if (searchQuery) params.append('search', searchQuery);
      
      const response = await fetch(`${API_URL}/api/garage/customers/?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      
      if (data.success) {
        setCustomers(data.customers || []);
      }
    } catch (error) {
      console.error('Error fetching customers:', error);
      toast.error('Failed to load customers');
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomerDetails = async (customer) => {
    setSelectedCustomer(customer);
    setLoadingDetails(true);
    setEditing(false);
    setEditForm(customer);
    
    try {
      const token = localStorage.getItem('garage_token');
      
      // Fetch jobs and vehicles in parallel
      const [jobsRes, vehiclesRes] = await Promise.all([
        fetch(`${API_URL}/api/garage/customers/${customer.id}/jobs`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/garage/customers/${customer.id}/vehicles`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);
      
      const jobsData = await jobsRes.json();
      const vehiclesData = await vehiclesRes.json();
      
      setCustomerJobs(jobsData.jobs || []);
      setCustomerVehicles(vehiclesData.vehicles || []);
    } catch (error) {
      console.error('Error fetching customer details:', error);
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleSaveCustomer = async () => {
    try {
      const token = localStorage.getItem('garage_token');
      const response = await fetch(`${API_URL}/api/garage/customers/${selectedCustomer.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(editForm)
      });
      
      const data = await response.json();
      if (data.success) {
        setSelectedCustomer(data.customer);
        setEditing(false);
        toast.success('Customer updated');
        fetchCustomers();
      }
    } catch (error) {
      toast.error('Failed to update customer');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'delivered': 'bg-green-500',
      'dropped': 'bg-green-500',
      'work_in_progress': 'bg-blue-500',
      'pending_estimate': 'bg-yellow-500',
      'estimate_sent': 'bg-orange-500',
      'customer_approved': 'bg-emerald-500',
      'qc': 'bg-purple-500'
    };
    return colors[status] || 'bg-gray-500';
  };

  return (
    <div className={`min-h-screen ${theme.pageBg} p-6`}>
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className={`text-2xl font-bold ${theme.textPrimary}`}>Customers</h1>
            <p className={theme.textSecondary}>Manage your customer database</p>
          </div>
          
          {/* Search */}
          <div className="relative w-full md:w-80">
            <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${theme.textMuted}`} />
            <Input
              placeholder="Search by name, phone, email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className={`pl-10 ${theme.inputBg}`}
            />
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Customer List */}
          <div className="lg:col-span-1 space-y-4">
            <Card className={theme.cardBg}>
              <CardHeader className="pb-2">
                <CardTitle className={`text-lg ${theme.textPrimary}`}>
                  <Users className="w-5 h-5 inline mr-2" />
                  All Customers ({customers.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 max-h-[600px] overflow-y-auto">
                {loading ? (
                  <div className="text-center py-8">
                    <div className="animate-spin w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full mx-auto" />
                  </div>
                ) : customers.length === 0 ? (
                  <div className={`text-center py-8 ${theme.textMuted}`}>
                    <Users className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>No customers found</p>
                    <p className="text-sm">Customers are auto-created when jobs are created</p>
                  </div>
                ) : (
                  customers.map((customer) => (
                    <div
                      key={customer.id}
                      onClick={() => fetchCustomerDetails(customer)}
                      className={`p-3 rounded-lg cursor-pointer transition-all ${
                        selectedCustomer?.id === customer.id 
                          ? 'bg-orange-500/20 border border-orange-500/50' 
                          : `${theme.hoverBg} border border-transparent`
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className={`font-medium ${theme.textPrimary}`}>{customer.name}</p>
                          <p className={`text-sm ${theme.textMuted}`}>
                            <Phone className="w-3 h-3 inline mr-1" />
                            {customer.phone}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className={`text-xs ${theme.textMuted}`}>{customer.total_jobs} jobs</p>
                          {customer.total_spent > 0 && (
                            <p className="text-xs text-green-500">
                              ₹{customer.total_spent.toLocaleString()}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </CardContent>
            </Card>
          </div>

          {/* Customer Details */}
          <div className="lg:col-span-2">
            {selectedCustomer ? (
              <div className="space-y-4">
                {/* Customer Info Card */}
                <Card className={theme.cardBg}>
                  <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle className={theme.textPrimary}>Customer Details</CardTitle>
                    <div className="flex gap-2">
                      {editing ? (
                        <>
                          <Button size="sm" variant="outline" onClick={() => setEditing(false)}>
                            <X className="w-4 h-4 mr-1" /> Cancel
                          </Button>
                          <Button size="sm" onClick={handleSaveCustomer}>
                            <Save className="w-4 h-4 mr-1" /> Save
                          </Button>
                        </>
                      ) : (
                        <Button size="sm" variant="outline" onClick={() => setEditing(true)}>
                          <Edit2 className="w-4 h-4 mr-1" /> Edit
                        </Button>
                      )}
                      <Button size="sm" variant="ghost" onClick={() => setSelectedCustomer(null)}>
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {editing ? (
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <label className={`text-sm ${theme.textMuted}`}>Name</label>
                          <Input
                            value={editForm.name || ''}
                            onChange={(e) => setEditForm({...editForm, name: e.target.value})}
                            className={theme.inputBg}
                          />
                        </div>
                        <div>
                          <label className={`text-sm ${theme.textMuted}`}>Phone</label>
                          <Input
                            value={editForm.phone || ''}
                            onChange={(e) => setEditForm({...editForm, phone: e.target.value})}
                            className={theme.inputBg}
                          />
                        </div>
                        <div>
                          <label className={`text-sm ${theme.textMuted}`}>Email</label>
                          <Input
                            value={editForm.email || ''}
                            onChange={(e) => setEditForm({...editForm, email: e.target.value})}
                            className={theme.inputBg}
                          />
                        </div>
                        <div>
                          <label className={`text-sm ${theme.textMuted}`}>GST Number (Optional)</label>
                          <Input
                            value={editForm.gst_number || ''}
                            onChange={(e) => setEditForm({...editForm, gst_number: e.target.value})}
                            className={theme.inputBg}
                          />
                        </div>
                        <div className="md:col-span-2">
                          <label className={`text-sm ${theme.textMuted}`}>Address</label>
                          <Input
                            value={editForm.address || ''}
                            onChange={(e) => setEditForm({...editForm, address: e.target.value})}
                            className={theme.inputBg}
                          />
                        </div>
                      </div>
                    ) : (
                      <div className="grid md:grid-cols-2 gap-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-orange-500/20 flex items-center justify-center">
                            <Users className="w-5 h-5 text-orange-500" />
                          </div>
                          <div>
                            <p className={`font-semibold ${theme.textPrimary}`}>{selectedCustomer.name}</p>
                            <p className={`text-sm ${theme.textMuted}`}>Customer</p>
                          </div>
                        </div>
                        <div className="space-y-2">
                          <p className={`text-sm ${theme.textSecondary}`}>
                            <Phone className="w-4 h-4 inline mr-2" />
                            {selectedCustomer.phone}
                          </p>
                          {selectedCustomer.email && (
                            <p className={`text-sm ${theme.textSecondary}`}>
                              <Mail className="w-4 h-4 inline mr-2" />
                              {selectedCustomer.email}
                            </p>
                          )}
                          {selectedCustomer.address && (
                            <p className={`text-sm ${theme.textSecondary}`}>
                              <MapPin className="w-4 h-4 inline mr-2" />
                              {selectedCustomer.address}
                            </p>
                          )}
                        </div>
                        <div className={`p-3 rounded-lg ${theme.successBg}`}>
                          <p className={`text-sm ${theme.textMuted}`}>Total Jobs</p>
                          <p className={`text-2xl font-bold ${theme.successText}`}>{selectedCustomer.total_jobs}</p>
                        </div>
                        <div className={`p-3 rounded-lg ${theme.infoBg}`}>
                          <p className={`text-sm ${theme.textMuted}`}>Total Spent</p>
                          <p className={`text-2xl font-bold ${theme.infoText}`}>
                            ₹{(selectedCustomer.total_spent || 0).toLocaleString()}
                          </p>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Customer's Vehicles */}
                <Card className={theme.cardBg}>
                  <CardHeader>
                    <CardTitle className={`text-lg ${theme.textPrimary}`}>
                      <Car className="w-5 h-5 inline mr-2" />
                      Vehicles ({customerVehicles.length})
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {loadingDetails ? (
                      <div className="text-center py-4">
                        <div className="animate-spin w-6 h-6 border-2 border-orange-500 border-t-transparent rounded-full mx-auto" />
                      </div>
                    ) : customerVehicles.length === 0 ? (
                      <p className={`text-center py-4 ${theme.textMuted}`}>No vehicles registered</p>
                    ) : (
                      <div className="grid md:grid-cols-2 gap-3">
                        {customerVehicles.map((vehicle) => (
                          <div 
                            key={vehicle.id}
                            className={`p-3 rounded-lg border ${theme.border} ${theme.hoverBg} cursor-pointer`}
                            onClick={() => navigate('/garage/vehicles', { state: { vehicleId: vehicle.id }})}
                          >
                            <p className={`font-mono font-bold ${theme.textPrimary}`}>
                              {vehicle.registration_number}
                            </p>
                            <p className={`text-sm ${theme.textMuted}`}>
                              {vehicle.make} {vehicle.model} {vehicle.year && `(${vehicle.year})`}
                            </p>
                            <p className={`text-xs ${theme.textMuted}`}>
                              {vehicle.total_jobs} services
                            </p>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Job History */}
                <Card className={theme.cardBg}>
                  <CardHeader>
                    <CardTitle className={`text-lg ${theme.textPrimary}`}>
                      <History className="w-5 h-5 inline mr-2" />
                      Job History ({customerJobs.length})
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {loadingDetails ? (
                      <div className="text-center py-4">
                        <div className="animate-spin w-6 h-6 border-2 border-orange-500 border-t-transparent rounded-full mx-auto" />
                      </div>
                    ) : customerJobs.length === 0 ? (
                      <p className={`text-center py-4 ${theme.textMuted}`}>No job history</p>
                    ) : (
                      <div className="space-y-3">
                        {customerJobs.map((job) => (
                          <div 
                            key={job.id}
                            onClick={() => navigate(`/garage/jobs/${job.id}`)}
                            className={`p-3 rounded-lg border ${theme.border} ${theme.hoverBg} cursor-pointer flex items-center justify-between`}
                          >
                            <div>
                              <div className="flex items-center gap-2">
                                <span className={`w-2 h-2 rounded-full ${getStatusColor(job.status)}`} />
                                <p className={`font-medium ${theme.textPrimary}`}>{job.job_number}</p>
                              </div>
                              <p className={`text-sm ${theme.textMuted}`}>
                                {job.vehicle_number} • {job.vehicle_make} {job.vehicle_model}
                              </p>
                              <p className={`text-xs ${theme.textMuted}`}>
                                <Calendar className="w-3 h-3 inline mr-1" />
                                {new Date(job.created_at).toLocaleDateString()}
                              </p>
                            </div>
                            <div className="text-right">
                              {job.estimate?.grand_total > 0 && (
                                <p className="text-green-500 font-medium">
                                  ₹{job.estimate.grand_total.toLocaleString()}
                                </p>
                              )}
                              <ChevronRight className={`w-5 h-5 ${theme.textMuted}`} />
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            ) : (
              <Card className={`${theme.cardBg} h-full flex items-center justify-center`}>
                <CardContent className="text-center py-16">
                  <Users className={`w-16 h-16 mx-auto mb-4 ${theme.textMuted} opacity-50`} />
                  <p className={theme.textMuted}>Select a customer to view details</p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Customers;
